// Carriers come from A2000 and Deposco only (item 8, 2026-10-28): a pickup or a shipment names one
// from `routing-carriers` (A2000_Gold `vw_routing_carriers`), never a typed one. The view decides
// which codes are placeholders (ROUTING, COLLECT, TRUCK; Deposco NOTAVAILABLE, SHOP, General
// Freight) — Isaac still confirms them, so they live there, not here. Schedule pickup preselects
// the carrier every selected line already has (`routing-line-carriers`), else says what they have.

import type { LineView } from "./assemble";
import type { LineSource } from "./deposco";

/** A row of `routing-carriers`: one carrier code. */
export interface CarrierRow {
  source: LineSource;
  carrier_code: string;
  carrier_name: string;
  scac: string | null;
  uses: number;
  last_used: string | null;
  is_placeholder: number;
}

/** A row of `routing-line-carriers`: an open A2000 pick ticket's or a Deposco SO's own carrier. */
export interface LineCarrierRow {
  source: LineSource;
  /** The pick ticket # as text, or the SO #. */
  ref: string;
  carrier_code: string | null;
  /** Above 1: the pick ticket's lines name different codes (mixed). */
  carrier_count: number;
  bol_no: string | null;
  appointment_id: string | null;
}

/** One carrier the pickers offer. Deposco: one per name, its codes together (UPS Ground = UPS, UPSN, UPS2). */
export interface CarrierOption {
  key: string;
  source: LineSource;
  name: string;
  /** What is stored as `carrierCode`: the code, or the most used of a Deposco name's codes. */
  code: string;
  /** Every code that is this carrier. */
  codes: string[];
  scac: string;
  uses: number;
}

/** What a pickup or a shipment saves: the name in `carrier` (reports, the log), the code and its source beside it. */
export interface CarrierPick {
  carrier: string;
  carrierCode: string;
  carrierSource: LineSource;
}

const clean = (v: string | null | undefined) => String(v ?? "").trim();

/** The options: no placeholder; A2000 first, then Deposco; inside each, most used first. */
export function carrierOptions(rows: readonly CarrierRow[]): CarrierOption[] {
  const a2000: CarrierOption[] = [];
  const deposco = new Map<string, CarrierOption & { best: number }>();
  for (const r of rows) {
    const code = clean(r.carrier_code);
    if (Number(r.is_placeholder) === 1 || !code) continue;
    const name = clean(r.carrier_name) || code;
    const uses = Number(r.uses) || 0;
    if (r.source === "Deposco") {
      const key = `Deposco|${name.toUpperCase()}`;
      const had = deposco.get(key);
      if (!had) deposco.set(key, { key, source: "Deposco", name, code, codes: [code], scac: "", uses, best: uses });
      else {
        had.codes.push(code);
        had.uses += uses;
        if (uses > had.best) Object.assign(had, { code, best: uses });
      }
    } else a2000.push({ key: `A2000|${code}`, source: "A2000", name, code, codes: [code], scac: clean(r.scac), uses });
  }
  const byUse = (a: CarrierOption, b: CarrierOption) => b.uses - a.uses || a.name.localeCompare(b.name);
  return [...a2000.sort(byUse), ...[...deposco.values()].map(({ best: _best, ...o }) => o).sort(byUse)];
}

/** "PERFORMANCE TEAM · PERF · PRFM": what an option shows (SCAC only when there is one). */
export const carrierOptionText = (o: Pick<CarrierOption, "name" | "code" | "scac">) =>
  [o.name, o.code, o.scac].filter(Boolean).join(" · ");

/** Typing filters by name, code (any of a Deposco name's) or SCAC; it never offers a new value. */
export const carrierMatches = (o: CarrierOption, typed: string) => {
  const q = typed.trim().toUpperCase();
  return !q || [o.name, o.scac, ...o.codes].some((v) => v.toUpperCase().includes(q));
};

/** The option a stored or a line's code is, by source: a Deposco code finds its name's option. */
export const optionOfCode = (options: readonly CarrierOption[], source: LineSource, code: string | null | undefined) =>
  code ? options.find((o) => o.source === source && o.codes.includes(code.trim())) : undefined;

/** What a pickup or a shipment saves for an option. */
export const carrierPick = (o: CarrierOption): CarrierPick => ({
  carrier: o.name,
  carrierCode: o.code,
  carrierSource: o.source,
});

/** "Carrier PERFORMANCE TEAM (PERF)": how the log names a picked carrier; a typed one alone. */
export const carrierLogText = (p: { carrier: string; carrierCode?: string | null }) =>
  p.carrier && p.carrierCode && p.carrierCode !== p.carrier ? `${p.carrier} (${p.carrierCode})` : p.carrier;

/** The rows of a line's pick tickets (A2000) or SOs (Deposco), by ref. */
const rowsOfLine = (line: Pick<LineView, "source" | "pts">, byRef: ReadonlyMap<string, LineCarrierRow>) =>
  line.pts.filter((p) => !p.gone).map((p) => byRef.get(`${line.source}|${p.pickTicket}`));

/** The line carriers by `source|ref`, as the rules below read them. */
export const lineCarrierIndex = (rows: readonly LineCarrierRow[]) =>
  new Map(rows.map((r) => [`${r.source}|${clean(r.ref)}`, r]));

/** Schedule pickup's carrier: preselected, or empty with what the lines have. */
export interface DefaultCarrier {
  pick: CarrierOption | null;
  /** "Lines have: PERF (3) · ROUTING (2) · none (1)" — "" when one is preselected. */
  hint: string;
}

/**
 * The carrier every selected line already has: each line exactly one code, the same carrier for all
 * (a Deposco code counts as its name's), not a placeholder, and no pick ticket with several codes.
 * Otherwise none, and what the lines have — each carrier's name, or its code with no name; "none"
 * for a line without one.
 */
export function defaultCarrier(
  lines: readonly Pick<LineView, "source" | "pts">[],
  byRef: ReadonlyMap<string, LineCarrierRow>,
  options: readonly CarrierOption[],
  /** Every row of the carriers view (placeholders too), for names in the hint. */
  all: readonly CarrierRow[],
): DefaultCarrier {
  // A placeholder named like a real carrier (Deposco SHOP "UPS Ground") is said by its code.
  const realNames = new Set(options.map((o) => `${o.source}|${o.name.toUpperCase()}`));
  const nameOf = new Map(
    all.map((r) => {
      const name = clean(r.carrier_name);
      const real = realNames.has(`${r.source}|${name.toUpperCase()}`);
      return [
        `${r.source}|${clean(r.carrier_code)}`,
        Number(r.is_placeholder) === 1 && real ? clean(r.carrier_code) : name,
      ];
    }),
  );
  const counts = new Map<string, number>();
  const count = (label: string) => counts.set(label, (counts.get(label) ?? 0) + 1);
  let mixed = false;
  const picks = lines.map((line) => {
    const rows = rowsOfLine(line, byRef);
    if (rows.some((r) => r && Number(r.carrier_count) > 1)) mixed = true;
    const codes = [...new Set(rows.map((r) => clean(r?.carrier_code)).filter(Boolean))];
    if (!codes.length) count("none");
    for (const c of codes) count(nameOf.get(`${line.source}|${c}`) || c);
    // Deposco codes of one name are one carrier.
    const opts = [...new Set(codes.map((c) => optionOfCode(options, line.source, c) ?? null))];
    return codes.length && opts.length === 1 ? opts[0]! : null;
  });
  const first = picks[0];
  if (!mixed && first && picks.every((p) => p === first)) return { pick: first, hint: "" };
  const hint = [...counts]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([label, n]) => `${label} (${n})`)
    .join(" · ");
  return { pick: null, hint: hint ? `Lines have: ${hint}` : "" };
}

/** "A2000 BOL: 1234567, 1234570 +2 more": the selected A2000 lines' BOL #s, information only; "" with none. */
export function bolText(
  lines: readonly Pick<LineView, "source" | "pts">[],
  byRef: ReadonlyMap<string, LineCarrierRow>,
) {
  const bols = [
    ...new Set(
      lines
        .flatMap((l) => (l.source === "A2000" ? rowsOfLine(l, byRef).map((r) => clean(r?.bol_no)) : []))
        .filter(Boolean),
    ),
  ];
  if (!bols.length) return "";
  return `A2000 BOL: ${bols.slice(0, 3).join(", ")}${bols.length > 3 ? ` +${bols.length - 3} more` : ""}`;
}
